import { ListFilter, X } from "lucide-react";
import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DropdownMenu, DropdownMenuOption } from "../../components";
import { TABLE_ITEMS } from "./constants";
import { FilterProperty } from "./types";

interface ProductFilterProps {
  onFilterPropertyClick: (property: FilterProperty | null) => void;
  onFilterValueClick: (value: ReactNode) => void;
  filterProperty: FilterProperty | null;
  filterValue: ReactNode | null;
  filterByValueOptions: DropdownMenuOption[];
}

export function ProductFilter({
  onFilterPropertyClick,
  filterProperty,
  filterValue,
  onFilterValueClick,
  filterByValueOptions,
}: ProductFilterProps) {
  const { t } = useTranslation();
  const [filterByPropertyOpen, setFilterByPropertyOpen] = useState(false);
  const [filterByValueOpen, setFilterByValueOpen] = useState(false);

  const filterByPropertyOptions = useMemo(
    () =>
      TABLE_ITEMS.map((item) => ({
        label: item.label,
        onClick: () => {
          onFilterPropertyClick(item.property);
          setFilterByPropertyOpen(false);
        },
      })),
    [onFilterPropertyClick]
  );  

  return (
    <div className="flex gap-3 items-center">
      <div className="relative">
        <DropdownMenu
          options={filterByPropertyOptions}
          open={filterByPropertyOpen}
          onOpenChange={setFilterByPropertyOpen}
        >
          <div
            onClick={() => setFilterByPropertyOpen(true)}
            className="text-xs relative shadow-custom bg-white text-neutral-500 rounded-md border border-[rgba(0,0,0,0.02)] py-[8.5px] pl-3 pr-2 min-w-[200px] flex justify-between items-center"
          >
            {filterProperty ? ":" : ` ${t("filters.filter_by_property")}`}
            {!filterProperty && <ListFilter className="h-3 w-3 stroke-black" />}
          </div>
        </DropdownMenu>
        {filterProperty && (
          <span className="px-2 py-1 bg-slate-300 rounded-md text-xs text-black flex gap-1 items-center absolute z-10 right-2 transform top-1/2 -translate-y-1/2">
            <button onClick={() => onFilterPropertyClick(null)}>
              <X className="h-3 w-3" />
            </button>
            {TABLE_ITEMS.find(item => item.property === filterProperty)?.label || filterProperty}
          </span>
        )}
      </div>
      {filterProperty && (
        <div className="relative">
          <DropdownMenu
            options={filterByValueOptions}
            open={filterByValueOpen}
            onOpenChange={setFilterByValueOpen}
          >
            <div
              onClick={() => setFilterByValueOpen(true)}
              className="text-xs relative shadow-custom bg-white text-neutral-500 rounded-md border border-[rgba(0,0,0,0.02)] py-[8.5px] pl-3 pr-2 min-w-[200px] flex justify-between items-center"
            >
              {t("filters.filter")}
              {filterValue ? ":" : ` ${t("filters.filter_by_value")}`}
              {!filterValue && <ListFilter className="h-3 w-3 stroke-black" />}
            </div>
          </DropdownMenu>
          {filterValue && (
            <span className="px-2 py-1 bg-slate-300 rounded-md text-xs text-black flex gap-1 items-center absolute z-10 right-2 transform top-1/2 -translate-y-1/2">
              <button onClick={() => onFilterValueClick(null)}>
                <X className="h-3 w-3" />
              </button>
              {filterValue}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
